// ═══════════════════════════════════════════════════════════════════════════
// OAuthButtonRow — Google / X / Discord OAuth strip shared by Login + Register.
//
// Both pages were rendering an identical 3-button column (~60 lines each)
// that only differed by the i18n key for the label (`continueWithProvider`
// on Login, `signUpWithProvider` on Register). This component takes a
// `mode` prop and picks the right key, so the rest of the contract is
// shared.
//
// The strip lives on its own file rather than inside auth-form.module.css
// because it has its own behaviour (3 handlers, a `loading` flag, an
// optional className for outer-spacing tweaks) — encapsulating it as a
// component keeps the consumer call-site readable.
// ═══════════════════════════════════════════════════════════════════════════

import { useTranslation } from 'react-i18next';
import { GoogleIcon, XIcon, DiscordIcon } from './OAuthIcons';
import styles from '../../pages/auth/auth-form.module.css';

interface OAuthButtonRowProps {
  /** Which i18n key to use for the labels. */
  mode: 'login' | 'signup';
  onGoogle: () => void;
  onTwitter: () => void;
  onDiscord: () => void;
  /** Disables all 3 buttons (e.g. while one OAuth handshake is in flight). */
  loading?: boolean;
  /** Optional class merged onto the outer wrapper for spacing overrides. */
  className?: string;
}

export function OAuthButtonRow({
  mode,
  onGoogle,
  onTwitter,
  onDiscord,
  loading = false,
  className,
}: OAuthButtonRowProps) {
  const { t } = useTranslation();
  // `continueWithProvider` reads as "Continue with Google" — fits Login's
  // returning-user voice. `signUpWithProvider` reads as "Sign up with
  // Google" — fits Register's first-time voice. Both keys exist in all
  // 5 locales (verified by the parity script).
  const labelKey = mode === 'login' ? 'auth.continueWithProvider' : 'auth.signUpWithProvider';
  const labelDefaults: Record<'login' | 'signup', (provider: string) => string> = {
    login: (provider) => `Continue with ${provider}`,
    signup: (provider) => `Sign up with ${provider}`,
  };
  const wrapperClass = className ? `${styles.oauthList} ${className}` : styles.oauthList;

  return (
    <div className={wrapperClass}>
      <button
        type="button"
        onClick={onGoogle}
        disabled={loading}
        className={styles.oauthButton}
      >
        <GoogleIcon />
        {t(labelKey, { provider: 'Google', defaultValue: labelDefaults[mode]('Google') })}
      </button>

      <button
        type="button"
        onClick={onTwitter}
        disabled={loading}
        className={styles.oauthButton}
      >
        <XIcon />
        {t(labelKey, { provider: 'X', defaultValue: labelDefaults[mode]('X') })}
      </button>

      <button
        type="button"
        onClick={onDiscord}
        disabled={loading}
        className={styles.oauthButtonDiscord}
      >
        <DiscordIcon />
        {t(labelKey, { provider: 'Discord', defaultValue: labelDefaults[mode]('Discord') })}
      </button>
    </div>
  );
}

export default OAuthButtonRow;
